import { pdfjs } from 'react-pdf';
import { logger } from '../utils/logger';

const PDFJS_DOC_OPTS = {
    standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/standard_fonts/',
    useSystemFonts: true
};

const MIN_RECT_SIZE = 20;
const SNAPSHOT_PIXEL_RATIO = 2;

export interface EditorSnapshotRect {
    x: number;
    y: number;
    width: number;
    height: number;
    number: number;
    enteredNumber?: number;
    toleranceManuallySet?: boolean;
    /** Tolerance taken from drawing OCR (e.g. ISO 286 / explicit ±); same orange marker as manual */
    toleranceFromDrawingRecognition?: boolean;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const cleanHex = hex.replace('#', '');
    return {
        r: parseInt(cleanHex.substring(0, 2), 16),
        g: parseInt(cleanHex.substring(2, 4), 16),
        b: parseInt(cleanHex.substring(4, 6), 16)
    };
}

/** Tight crop around PDF render + markup so A4 embed uses almost the full page. */
function cropSnapshotCanvas(
    source: HTMLCanvasElement,
    pdfLeft: number,
    pdfTop: number,
    pdfW: number,
    pdfH: number,
    rects: EditorSnapshotRect[],
    pr: number,
    fullW: number,
    fullH: number
): HTMLCanvasElement {
    const strokePad = Math.ceil(3 * pr);
    const labelTopPad = Math.ceil(2 * pr);
    const labelBottomPad = Math.ceil(18 * pr);
    const dotPad = Math.ceil(10 * pr);

    let minX = pdfLeft;
    let minY = pdfTop;
    let maxX = pdfLeft + pdfW;
    let maxY = pdfTop + pdfH;

    for (const rect of rects) {
        const nx = (rect.width < 0 ? rect.x + rect.width : rect.x) * pr;
        const ny = (rect.height < 0 ? rect.y + rect.height : rect.y) * pr;
        const nw = Math.abs(rect.width) * pr;
        const nh = Math.abs(rect.height) * pr;

        minX = Math.min(minX, nx - strokePad);
        minY = Math.min(minY, ny - strokePad);
        maxX = Math.max(maxX, nx + nw + strokePad);
        maxY = Math.max(maxY, ny + nh + strokePad);

        if (Math.abs(rect.width) >= MIN_RECT_SIZE && Math.abs(rect.height) >= MIN_RECT_SIZE) {
            const labelY = ny + 5 * pr;
            minY = Math.min(minY, labelY - labelTopPad);
            maxY = Math.max(maxY, labelY + labelBottomPad);
            if (rect.toleranceManuallySet || rect.toleranceFromDrawingRecognition) {
                const brX = (rect.width >= 0 ? rect.x + rect.width : rect.x) * pr;
                const brY = (rect.height >= 0 ? rect.y + rect.height : rect.y) * pr;
                minX = Math.min(minX, brX - 6 * pr - dotPad);
                minY = Math.min(minY, brY - 6 * pr - dotPad);
                maxX = Math.max(maxX, brX - 6 * pr + dotPad);
                maxY = Math.max(maxY, brY - 6 * pr + dotPad);
            }
        }
    }

    minX = Math.max(0, Math.floor(minX));
    minY = Math.max(0, Math.floor(minY));
    maxX = Math.min(fullW, Math.ceil(maxX));
    maxY = Math.min(fullH, Math.ceil(maxY));

    let cw = maxX - minX;
    let ch = maxY - minY;
    if (cw < 8 || ch < 8) {
        return source;
    }

    const cropped = document.createElement('canvas');
    cropped.width = cw;
    cropped.height = ch;
    const cctx = cropped.getContext('2d');
    if (!cctx) {
        return source;
    }
    cctx.drawImage(source, minX, minY, cw, ch, 0, 0, cw, ch);
    return cropped;
}

/**
 * Renders the current PDF page and editor rectangles into a PNG (like a viewer screenshot).
 * Viewport letterboxing matches extractTextFromPdfJs / react-pdf Page fit.
 */
export async function buildEditorMarkupSnapshotPng(params: {
    file: File;
    pageNumber: number;
    stageWidth: number;
    stageHeight: number;
    rects: EditorSnapshotRect[];
    getToolColorByNumber: (toolNumber: number) => string;
}): Promise<Uint8Array | null> {
    const { file, pageNumber, stageWidth, stageHeight, rects, getToolColorByNumber } = params;
    if (!file || stageWidth <= 0 || stageHeight <= 0) {
        return null;
    }

    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({
            data: arrayBuffer.slice(0),
            ...PDFJS_DOC_OPTS
        }).promise;
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });

        const baseScale = Math.min(
            stageWidth / baseViewport.width,
            stageHeight / baseViewport.height
        );
        const renderScale = baseScale * SNAPSHOT_PIXEL_RATIO;
        const viewport = page.getViewport({ scale: renderScale });

        const pdfCanvas = document.createElement('canvas');
        pdfCanvas.width = Math.ceil(viewport.width);
        pdfCanvas.height = Math.ceil(viewport.height);
        const pdfCtx = pdfCanvas.getContext('2d');
        if (!pdfCtx) {
            return null;
        }

        const renderTask = page.render({ canvasContext: pdfCtx, viewport });
        await renderTask.promise;

        const outW = Math.round(stageWidth * SNAPSHOT_PIXEL_RATIO);
        const outH = Math.round(stageHeight * SNAPSHOT_PIXEL_RATIO);
        const merge = document.createElement('canvas');
        merge.width = outW;
        merge.height = outH;
        const ctx = merge.getContext('2d');
        if (!ctx) {
            return null;
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, outW, outH);
        const ox = (outW - pdfCanvas.width) / 2;
        const oy = (outH - pdfCanvas.height) / 2;
        ctx.drawImage(pdfCanvas, ox, oy);

        const pr = SNAPSHOT_PIXEL_RATIO;

        for (const rect of rects) {
            const toolColor = rect.enteredNumber != null
                ? getToolColorByNumber(rect.enteredNumber)
                : '#808080';
            const { r, g, b } = hexToRgb(toolColor);
            const nx = (rect.width < 0 ? rect.x + rect.width : rect.x) * pr;
            const ny = (rect.height < 0 ? rect.y + rect.height : rect.y) * pr;
            const nw = Math.abs(rect.width) * pr;
            const nh = Math.abs(rect.height) * pr;

            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.1)`;
            ctx.fillRect(nx, ny, nw, nh);
            ctx.strokeStyle = toolColor;
            ctx.lineWidth = 2 * pr;
            ctx.strokeRect(nx, ny, nw, nh);

            if (Math.abs(rect.width) >= MIN_RECT_SIZE && Math.abs(rect.height) >= MIN_RECT_SIZE) {
                ctx.font = `bold ${14 * pr}px sans-serif`;
                ctx.fillStyle = '#3c2eb4';
                ctx.textBaseline = 'top';
                ctx.fillText(String(rect.number), nx + 5 * pr, ny + 5 * pr);

                if (rect.toleranceManuallySet || rect.toleranceFromDrawingRecognition) {
                    const brX = (rect.width >= 0 ? rect.x + rect.width : rect.x) * pr;
                    const brY = (rect.height >= 0 ? rect.y + rect.height : rect.y) * pr;
                    ctx.beginPath();
                    ctx.arc(brX - 6 * pr, brY - 6 * pr, 4 * pr, 0, Math.PI * 2);
                    ctx.fillStyle = '#ff8c00';
                    ctx.fill();
                }
            }
        }

        const toExport = cropSnapshotCanvas(
            merge,
            ox,
            oy,
            pdfCanvas.width,
            pdfCanvas.height,
            rects,
            pr,
            outW,
            outH
        );

        const blob: Blob | null = await new Promise(resolve => {
            toExport.toBlob(b => resolve(b), 'image/png');
        });
        if (!blob) {
            return null;
        }
        return new Uint8Array(await blob.arrayBuffer());
    } catch (error) {
        logger.error('[editorMarkupSnapshot] Failed to build snapshot:', error);
        return null;
    }
}
